import React, { useState, useEffect, useMemo, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Sparkles, Gift, Check, Loader2, Copy } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const authHeaders = () => {
  const token = localStorage.getItem('customer_token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

// Palette that alternates so adjacent wheel slices stay visually distinct.
const SLICE_COLOURS = ['#1f6d62', '#a0da39', '#0FA49C', '#f5a623', '#e0685f', '#5b8fb9'];

// ============================================================================
// The wheel graphic — pure SVG, animated via transform: rotate() on the group.
// The pointer is fixed at 12 o'clock. To land slice `i` under the pointer,
// we rotate the wheel by `- (i * sliceAngle) - sliceAngle/2` plus 5 full
// turns of drama.
// ============================================================================
function Wheel({ prizes, spinning, targetIndex, size = 280 }) {
  const n = prizes.length;
  const slice = 360 / n;
  const r = size / 2;
  const cx = r;
  const cy = r;

  const targetAngle = useMemo(() => {
    if (targetIndex == null || targetIndex < 0) return 0;
    // 5 full turns + rotate so target slice's centre lines up with the top.
    const finalOffset = -(targetIndex * slice + slice / 2);
    return 360 * 5 + finalOffset;
  }, [targetIndex, slice]);

  const polar = (angleDeg, radius) => {
    const rad = ((angleDeg - 90) * Math.PI) / 180;
    return [cx + radius * Math.cos(rad), cy + radius * Math.sin(rad)];
  };

  return (
    <div className="relative" style={{ width: size, height: size }}>
      {/* Pointer at top */}
      <div
        className="absolute left-1/2 -top-2 -translate-x-1/2 z-10"
        style={{ filter: 'drop-shadow(0 3px 4px rgba(0,0,0,0.4))' }}
      >
        <div
          className="w-0 h-0"
          style={{
            borderLeft: '14px solid transparent',
            borderRight: '14px solid transparent',
            borderTop: '22px solid #1a1a1a',
          }}
        />
      </div>

      <motion.svg
        width={size}
        height={size}
        viewBox={`0 0 ${size} ${size}`}
        className="drop-shadow-xl rounded-full"
        animate={{ rotate: spinning ? targetAngle : 0 }}
        transition={spinning ? { duration: 5.2, ease: [0.17, 0.67, 0.2, 1.02] } : { duration: 0 }}
      >
        <circle cx={cx} cy={cy} r={r - 2} fill="#111" />
        {prizes.map((p, i) => {
          const startAngle = i * slice;
          const endAngle = (i + 1) * slice;
          const [x1, y1] = polar(startAngle, r - 6);
          const [x2, y2] = polar(endAngle, r - 6);
          const largeArc = slice > 180 ? 1 : 0;
          const path = [
            `M ${cx} ${cy}`,
            `L ${x1} ${y1}`,
            `A ${r - 6} ${r - 6} 0 ${largeArc} 1 ${x2} ${y2}`,
            'Z',
          ].join(' ');
          const fill = SLICE_COLOURS[i % SLICE_COLOURS.length];
          const midAngle = startAngle + slice / 2;
          const [tx, ty] = polar(midAngle, r * 0.62);
          return (
            <g key={p.id || i}>
              <path d={path} fill={fill} stroke="#fff" strokeWidth={2} />
              <text
                x={tx}
                y={ty}
                fill="#ffffff"
                fontSize={Math.max(11, r / 12)}
                fontWeight={900}
                textAnchor="middle"
                dominantBaseline="middle"
                transform={`rotate(${midAngle} ${tx} ${ty})`}
                style={{ letterSpacing: '0.5px', paintOrder: 'stroke', stroke: 'rgba(0,0,0,0.35)', strokeWidth: 2 }}
              >
                {p.label}
              </text>
            </g>
          );
        })}
        <circle cx={cx} cy={cy} r={16} fill="#111" stroke="#a0da39" strokeWidth={3} />
        <circle cx={cx} cy={cy} r={4} fill="#a0da39" />
      </motion.svg>
    </div>
  );
}


// ============================================================================
// Public wheel card — rendered on the account page.
// States:
//   - hidden           → feature disabled OR wheel already spun long ago
//   - idle             → big "Spin Ted's Wheel" CTA card
//   - modal spinning   → wheel + spin button (opens on click)
//   - modal resolved   → confetti + prize card with code (in same modal)
//   - already-spun     → compact prize card back on the account page
// ============================================================================
export const TedsWheelCard = ({ onWin }) => {
  const [state, setState] = useState(null); // {enabled, has_spun, spin, prizes}
  const [open, setOpen] = useState(false);
  const [spinning, setSpinning] = useState(false);
  const [targetIndex, setTargetIndex] = useState(null);
  const [resolvedPrize, setResolvedPrize] = useState(null);
  const [spinErr, setSpinErr] = useState('');
  const settleTimer = useRef(null);

  useEffect(() => {
    axios.get(`${API}/customer/teds-wheel`, { headers: authHeaders() })
      .then(res => setState(res.data))
      .catch(() => setState({ enabled: false }));
    return () => { if (settleTimer.current) clearTimeout(settleTimer.current); };
  }, []);

  if (!state || !state.enabled) return null;

  // Once the customer has spun, the prize now lives in their Vouchers section
  // further down the account page — no need to duplicate it as a top banner.
  if (state.has_spun && !open) return null;

  const startSpin = async () => {
    setOpen(true);
    setSpinErr('');
  };

  const doSpin = async () => {
    if (spinning) return; // guard against double-taps before setSpinning fires
    setSpinning(true);    // hide the button + mark busy immediately
    setSpinErr('');
    setResolvedPrize(null);
    try {
      const { data } = await axios.post(`${API}/customer/teds-wheel/spin`, {}, { headers: authHeaders() });
      setTargetIndex(data.prize_index);
      // After the spin animation finishes, reveal the prize + refresh state.
      settleTimer.current = setTimeout(() => {
        setSpinning(false);
        setResolvedPrize(data.prize);
        setState(prev => ({ ...prev, has_spun: true, spin: data.prize }));
        // If a redeemable voucher was created, tell the parent so it can
        // refresh the Vouchers section and jump the customer there.
        if (data.voucher) onWin?.(data.voucher);
      }, 5400);
    } catch (err) {
      setSpinning(false);
      setSpinErr(err.response?.data?.detail || 'Could not spin — please try again in a moment.');
    }
  };

  const closeModal = () => {
    setOpen(false);
    setSpinning(false);
    setResolvedPrize(null);
    setTargetIndex(null);
  };

  return (
    <>
      {/* Idle CTA on the account page */}
      <button
        type="button"
        onClick={startSpin}
        className="w-full text-left rounded-2xl overflow-hidden relative group cursor-pointer border-2 border-[#a0da39] shadow-lg"
        style={{
          background: 'linear-gradient(135deg, #1f6d62 0%, #0FA49C 50%, #1f6d62 100%)',
        }}
        data-testid="teds-wheel-cta"
      >
        <div className="absolute inset-0 opacity-20" style={{
          backgroundImage: 'radial-gradient(circle at 20% 20%, #a0da39 0%, transparent 40%), radial-gradient(circle at 80% 80%, #f5a623 0%, transparent 40%)',
        }} />
        <div className="relative flex items-center gap-4 p-5">
          <div className="w-16 h-16 rounded-full bg-[#a0da39] flex items-center justify-center flex-shrink-0 shadow-xl">
            <Gift className="w-8 h-8 text-[#1a1a1a]" />
          </div>
          <div className="flex-1">
            <p className="text-[10px] uppercase tracking-[3px] text-[#a0da39] font-bold mb-0.5">One free spin</p>
            <h3 className="text-xl font-black text-white leading-tight">Spin Ted&apos;s Wheel!</h3>
            <p className="text-xs text-white/85 mt-1">Tap to reveal your welcome prize — you won&apos;t get another chance.</p>
          </div>
          <Sparkles className="w-6 h-6 text-[#a0da39] flex-shrink-0" />
        </div>
      </button>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/80 z-[100] flex items-center justify-center p-4"
            onClick={resolvedPrize ? closeModal : undefined}
            data-testid="teds-wheel-modal"
          >
            <motion.div
              initial={{ scale: 0.9, y: 20 }}
              animate={{ scale: 1, y: 0 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="bg-gradient-to-b from-[#1f6d62] to-[#14544b] rounded-3xl p-6 sm:p-8 max-w-md w-full text-center relative border-2 border-[#a0da39]"
              onClick={(e) => e.stopPropagation()}
            >
              {!spinning && !resolvedPrize && (
                <>
                  <p className="text-[10px] uppercase tracking-[3px] text-[#a0da39] font-bold mb-1">Ted&apos;s Welcome Wheel</p>
                  <h2 className="text-2xl font-black text-white mb-1">Take your one free spin</h2>
                  <p className="text-white/80 text-sm mb-5">Whatever you land on is yours to keep. Ready?</p>
                </>
              )}
              {spinning && (
                <>
                  <p className="text-[10px] uppercase tracking-[3px] text-[#a0da39] font-bold mb-1">Spinning…</p>
                  <h2 className="text-2xl font-black text-white mb-1">Fingers crossed!</h2>
                  <p className="text-white/80 text-sm mb-5">Ted&apos;s picking your prize.</p>
                </>
              )}
              {resolvedPrize && (
                <>
                  <div className="text-6xl mb-2">🎉</div>
                  <h2 className="text-2xl font-black text-white mb-1">You won!</h2>
                  <p className="text-3xl font-black text-[#a0da39] mb-2" data-testid="wheel-resolved-label">{resolvedPrize.prize_label}</p>
                  {resolvedPrize.prize_description && (
                    <p className="text-white/80 text-sm mb-2">{resolvedPrize.prize_description}</p>
                  )}
                  {resolvedPrize.prize_claim_at && (
                    <p className="text-[11px] uppercase tracking-widest text-[#a0da39] font-bold mb-2" data-testid="wheel-resolved-claim-at">
                      Claim at {resolvedPrize.prize_claim_at.replace(/^https?:\/\//, '')}
                    </p>
                  )}
                </>
              )}
              <div className="flex justify-center my-3">
                <Wheel prizes={state.prizes} spinning={spinning || !!resolvedPrize} targetIndex={targetIndex} size={280} />
              </div>
              {!spinning && !resolvedPrize && (
                <button
                  type="button"
                  onClick={doSpin}
                  className="w-full mt-4 bg-[#a0da39] hover:bg-[#b4e856] text-[#1f3d2f] font-black text-lg rounded-2xl px-6 py-4 shadow-xl transition-colors"
                  data-testid="teds-wheel-spin-btn"
                >
                  <Sparkles className="w-5 h-5 inline mr-2" />
                  SPIN THE WHEEL
                </button>
              )}
              {spinErr && (
                <p className="text-red-300 text-sm mt-3">{spinErr}</p>
              )}
              {resolvedPrize && (
                <PrizeCodeBlock code={resolvedPrize.prize_code} onClose={closeModal} />
              )}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};


// ============================================================================
// Persistent "already spun" prize card on the account page
// ============================================================================
const SpunPrizeCard = ({ spin }) => {
  const [copied, setCopied] = useState(false);
  const copy = () => {
    if (!spin?.prize_code) return;
    navigator.clipboard.writeText(spin.prize_code);
    setCopied(true);
    setTimeout(() => setCopied(false), 1600);
    toast.success('Code copied!');
  };
  return (
    <div
      className="rounded-2xl bg-gradient-to-br from-[#1f6d62] to-[#14544b] border-2 border-dashed border-[#a0da39] p-5 shadow-lg"
      data-testid="teds-wheel-spun-card"
    >
      <div className="flex items-center gap-2 mb-1">
        <Gift className="w-4 h-4 text-[#a0da39]" />
        <p className="text-[10px] uppercase tracking-[3px] text-[#a0da39] font-bold">Your Ted&apos;s Wheel prize</p>
      </div>
      <p className="text-2xl font-black text-white mb-1" data-testid="wheel-spun-label">{spin.prize_label}</p>
      {spin.prize_description && (
        <p className="text-white/80 text-xs mb-3">{spin.prize_description}</p>
      )}
      {spin.prize_claim_at && (
        <p className="text-[10px] uppercase tracking-widest text-[#a0da39] font-bold mb-2" data-testid="wheel-spun-claim-at">
          Claim at {spin.prize_claim_at.replace(/^https?:\/\//, '')}
        </p>
      )}
      {spin.prize_code ? (
        <button
          type="button"
          onClick={copy}
          className="w-full flex items-center justify-between gap-3 bg-[#a0da39] hover:bg-[#b4e856] text-[#1f3d2f] font-black rounded-xl px-4 py-3 text-lg tracking-widest uppercase transition-colors"
          data-testid="wheel-spun-code"
        >
          <span className="flex-1 text-center">{spin.prize_code}</span>
          <span className="text-xs font-bold uppercase tracking-widest bg-white/60 rounded-md px-2 py-1 inline-flex items-center gap-1">
            {copied ? <><Check className="w-3 h-3" /> Copied</> : <><Copy className="w-3 h-3" /> Copy</>}
          </span>
        </button>
      ) : (
        <p className="text-white/60 italic text-sm">No code attached to this prize — mention it in-store.</p>
      )}
    </div>
  );
};


const PrizeCodeBlock = ({ code, onClose }) => {
  const [copied, setCopied] = useState(false);
  const copy = () => {
    if (!code) return;
    navigator.clipboard.writeText(code);
    setCopied(true);
    setTimeout(() => setCopied(false), 1600);
    toast.success('Code copied!');
  };
  return (
    <div className="mt-4 space-y-2">
      {code && (
        <button
          type="button"
          onClick={copy}
          className="w-full flex items-center justify-between gap-3 bg-[#a0da39] hover:bg-[#b4e856] text-[#1f3d2f] font-black rounded-xl px-4 py-3 text-lg tracking-widest uppercase"
          data-testid="wheel-modal-code"
        >
          <span className="flex-1 text-center">{code}</span>
          <span className="text-xs font-bold uppercase tracking-widest bg-white/60 rounded-md px-2 py-1 inline-flex items-center gap-1">
            {copied ? <><Check className="w-3 h-3" /> Copied</> : <><Copy className="w-3 h-3" /> Copy</>}
          </span>
        </button>
      )}
      <button
        type="button"
        onClick={onClose}
        className="w-full text-white/80 hover:text-white text-sm underline pt-1"
        data-testid="wheel-modal-close"
      >
        Great, thanks Ted!
      </button>
    </div>
  );
};

export default TedsWheelCard;
